<template>
  <div
    v-if="isOpen && showBars"
    class="c-thumbnails"
    v-shortkey="['esc']"
    @shortkey="closeThumbnails"
  >

    <button
      ref="btnPrev"
      @click="previous()"
      class="button arrow arrow-prev"
      :aria-label="`${$t('GO_TO')} ${$t('NEXT_THUMBNAILS_ROW')}`"
    />
    <the-carousel
      v-if="!bookRender.twoPages"
      ref="flickity"
      :options="flickityOptions"
      @init="initCarousel()"
      class="carousel"
    >
      <button
        v-for="page in book.content"
        :key="getPageByIndex(page.position)"
        class="c-thumbnails_carousel carousel-cell"
        :aria-label="`${$t('GO_TO')} ${$t('PAGE')} ${getPageByIndex(page.position)}`"
        @click="bookRender.getPage(getPageByIndex(page.position))"
      >
        <star-thumbnail-icon class="carousel-cell-star" v-if="checkBookmark(getPageByIndex(page.position))"/>
        <img
          class="carousel-cell-thumbnail"
          :data-flickity-lazyload="`${$root.$config.booksync_url}/pubs/${book._id}/thumbnail/page${getPageByIndex(page.position)}.png?token=${encodeURIComponent(accessToken)}`"
          :alt="`${$t('PAGE')} ${getPageByIndex(page.position)}`"
        >
        <div class="carousel-cell-shadow"></div>
        <span class="carousel-cell-label">
          {{getPageByIndex(page.position)}}
        </span>
      </button>
    </the-carousel>

    <the-carousel
      v-else
      ref="flickity"
      :options="flickityOptions"
      @init="initCarousel()"
      class="carousel"
    >
      <button
        v-for="(pages, i) in itemGroupies"
        :key="i"
        class="c-thumbnails_carousel carousel-cell"
        :class="i > 0 && i < itemGroupies.length - 1 || i === itemGroupies.length - 1 && pages.length === 2 ? 'c-thumbnails_carousel--pair' : ''"
        :aria-label="`${$t('GO_TO')} ${$t('PAGE')} ${getPageByIndex(pages[0].position)}`"
        @click="bookRender.getPage(getPageByIndex(pages[0].position))"
      >
        <div v-for="page in pages" :key="getPageByIndex(page.position)" class="c-thumbnails__carousel-cell-child">
          <star-thumbnail-icon class="carousel-cell-star" v-if="checkBookmark(getPageByIndex(page.position))"/>
          <img
            class="carousel-cell-thumbnail"
            :data-flickity-lazyload="`${$root.$config.booksync_url}/pubs/${book._id}/thumbnail/${getPageName(page.name)}.png?token=${encodeURIComponent(accessToken)}`"
            :alt="`${$t('PAGE')} ${getPageByIndex(page.position)}`"
          >
          <div class="carousel-cell-shadow"></div>
          <span class="carousel-cell-label">
            {{getPageByIndex(page.position)}}
          </span>
        </div>
      </button>
    </the-carousel>
    <button
      @click="next()"
      class="button arrow arrow-next"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'

import TheCarousel from '@/components/TheCarousel'
import StarThumbnailIcon from '@/assets/svg/StarThumbnailIcon'

import { getPageByIndex, getBookMetadata, getPageName } from '@/utils/utils'

export default {
  name: 'BookThumbnails',

  props: {
    isOpen: Boolean,
    showBars: Boolean,
    book: Object,
    bookRender: Object,
    accessToken: String
  },

  data () {
    return {
      flickityOptions: {
        draggable: false,
        groupCells: true,
        prevNextButtons: false,
        pageDots: false,
        accessibility: false,
        wrapAround: false,
        contain: false,
        cellAlign: 'left',
        lazyLoad: 1,
        rightToLeft: getBookMetadata(this.book, '-dbook-right-to-left', false)
      }
    }
  },

  computed: {
    itemGroupies() {
      const itemsForGrouping = [...this.book.content]
      const firstItem = [itemsForGrouping.splice(0, 1)]
      const lastItem = [itemsForGrouping.splice(itemsForGrouping.length - 1, 1)]
      let groupies

      groupies = itemsForGrouping.reduce((items, currentItem) => {
        const lastItemPosition = items.length - 1

        if (!items[lastItemPosition]) {
          items.push([currentItem])

          return items
        }

        if (items[lastItemPosition].length === 1) {
          items[lastItemPosition].push(currentItem)
        } else {
          items.push([currentItem])
        }

        return items;
      }, [])

      if (groupies[groupies.length - 1].length === 1) {
        groupies[groupies.length - 1].push(lastItem[0][0])

        return [].concat(firstItem, groupies)
      }

      return [].concat(firstItem, groupies, lastItem)
    },

    ...mapState('bookmarks', {
      bookmarks: state => state.bookmarks
    })
  },

  watch: {
    'bookRender.currentPageNumber' (newVal, prevVal) {
      if (this.isOpen && this.showBars) {
        this.highlight(newVal, prevVal)
        this.getSelectedCell(newVal)
      }
    },
    'bookRender.twoPages' (hasTwoPages) {
      if (this.$refs.flickity) {
        this.$refs.flickity.$flickity.destroy()
        setTimeout(() => {
          this.$refs.flickity.init()
        }, 0)
      }
    },
    isOpen(val) {
      if (val) {
        this.$nextTick(() => {
          if (!this.$refs.btnPrev) return
          this.$refs.btnPrev.focus()
        })
      }
    }
  },

  components: {
    'the-carousel': TheCarousel,
    'star-thumbnail-icon': StarThumbnailIcon
  },

  methods: {
    closeThumbnails() {
      this.$emit('close-thumbnails')
    },

    getPageByIndex,
    getPageName,
    findIndex(val) {
      let needle = null

      this.itemGroupies.forEach((ig, i) => {
        ig.forEach(item => {
          if (getPageByIndex(item.position) === val) {
            needle = i
          }
        })
      })

      return needle
    },

    initCarousel () {
      this.highlight(this.bookRender.currentPageNumber)
      this.getSelectedCell(this.bookRender.currentPageNumber)
    },

    getSelectedCell (newVal) {
      this.$refs.flickity.selectCell(this.bookRender.twoPages && newVal !== 1 && newVal !== this.bookRender.getTotalPages() ? this.findIndex(newVal) : newVal - 1)
    },

    highlight (newVal, prevVal) {
      if (this.$refs.flickity) {
        if (prevVal) {
          if (this.bookRender.twoPages && prevVal !== 1) {
            this.$refs.flickity.$flickity.cells[this.findIndex(prevVal)].element.classList.remove('current')
            this.$refs.flickity.$flickity.cells[this.findIndex(prevVal)].element.setAttribute('aria-current', 'false')
          } else {
            this.$refs.flickity.$flickity.cells[prevVal - 1].element.classList.remove('current')
            this.$refs.flickity.$flickity.cells[prevVal - 1].element.setAttribute('aria-current', 'false')
          }
        }
        if (this.bookRender.twoPages && newVal !== 0) {
          this.$refs.flickity.$flickity.cells[this.findIndex(newVal)].element.classList.add('current')
          this.$refs.flickity.$flickity.cells[this.findIndex(newVal)].element.setAttribute('aria-current', 'true')
        } else {
          this.$refs.flickity.$flickity.cells[newVal - 1].element.classList.add('current')
          this.$refs.flickity.$flickity.cells[newVal - 1].element.setAttribute('aria-current', 'true')
        }
      }
    },

    next () {
      this.$refs.flickity.next()
    },

    previous () {
      this.$refs.flickity.previous()
    },

    checkBookmark (currentPage) {
      if (this.bookmarks) {
        for (let bookmark of this.bookmarks) {
          if (bookmark.page === currentPage) {
            return true
          }
        }
        return false
      }
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/scss/_vars.scss';
@import '@/assets/scss/_mixin.scss';

  .c-thumbnails {
    position: fixed;
    bottom: 49px;
    height: 200px;
    left: 0;
    right: 0;
    background: $slider-grey;
    padding: 10px 80px 10px 80px;
    z-index: 10;

    @media (min-width: 1366px) {
      left: 110px;
    }
  }
  .c-thumbnails_carousel {
    @include btnReset();
    width: 124px;
    height: 170px;
    margin: 10px 10px 0 7px;
    cursor: pointer;
    position: relative;

    &--pair {
      display: flex;
      width: 250px;

      .c-thumbnails__carousel-cell-child:first-child .carousel-cell-thumbnail {
        left: auto;
        right: 0;
        margin-right: 0;
      }

      .c-thumbnails__carousel-cell-child:nth-child(2) .carousel-cell-thumbnail {
        margin-left: 0;
      }
    }
  }

  .c-thumbnails__carousel-cell-child {
    position: relative;
    width: 124px;
    height: 170px;

    .c-thumbnails_carousel--pair & {
      &:nth-child(1) .carousel-cell-thumbnail {
        border-right: none!important;
      }
      &:nth-child(2) .carousel-cell-thumbnail {
        border-left: none!important;
      }
    }
  }

  .carousel-cell {
    @include a11yOutline();
    outline-offset: -4px;
  }
  .carousel-cell.current .carousel-cell-thumbnail {
    border: 3px solid $orange;
  }
  .carousel-cell-thumbnail {
    background: #f7f7f7;
    width: 116px;
    height: 124px;
    position: absolute;
    top: 0;
    left: 0;
    margin: 4px;
  }
  .carousel-cell-label {
    color: $blue;
    font-weight: bold;
    position: absolute;
    bottom: 1px;
  }
  .arrow {
    background: transparent;
    position: absolute;
    top: 38%;
    height: 0;
    width: 0;
    padding: 0;
    margin: 0 0.5rem;
    border: 20px transparent solid;
    &:focus, &:active {
      box-shadow: none !important;
    }

    @include a11yOutline();
  }
  .arrow-prev {
    left: -10px;
    border-right: 20px $blue solid;
  }
  .arrow-prev {
    &:hover, &:active, &:focus {
      border-color: transparent;
      border-right-color: $lighter-dark-blue;
    }
  }
  .arrow-next {
    right: -10px;
    border-left: 20px $blue solid;
  }
  .arrow-next {
    &:hover, &:active, &:focus {
      border-color: transparent;
      border-left-color: $lighter-dark-blue;
    }
  }
  .carousel-cell-star {
    position: absolute;
    top: -12px;
    right: 0;
    z-index: 1;
  }
  .carousel-cell-pair:nth-child(odd):not(:first-child):not(:last-child) {
    .carousel-cell-star {
      right: 0;
    }
  }
  .carousel-cell-pair:nth-child(even):not(:first-child):not(:last-child) {
    .carousel-cell-star {
      left: 0;
    }
    .carousel-cell-shadow {
      box-shadow: inset -5px 0px 8px -6px $dark-grey;
    }
  }
  .carousel-cell-shadow {
    position: absolute;
    width: 116px;
    height: 124px;
    box-shadow: inset 5px 0px 8px -6px $dark-grey;
    top: 4px;
    left: 4px;

    .c-thumbnails_carousel--pair & {
      left: 6px;
    }
  }
</style>
